/**
 * AttachmentsSection.tsx
 * 
 * Componente per la gestione degli allegati delle richieste nell'applicazione Ufficio Stile.
 * Supporta upload, download, visualizzazione e eliminazione di file allegati locali e globali.
 * Utilizza Mantine Dropzone per il caricamento e gestisce diversi tipi di file.
 * Integra allegati locali e cross-portal (globali) con badge distintivi.
 */

import { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Card,
  ActionIcon,
  Badge,
  Box,
  Progress,
  Alert,
  Modal,
  TextInput,
  Tabs,
  Divider,
  Tooltip,
} from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import {
  IconUpload,
  IconFile,
  IconDownload,
  IconX,
  IconPhoto,
  IconFileText,
  IconVideo,
  IconGlobe,
  IconBuilding,
  IconTrash
} from '@tabler/icons-react';
import {
  useGlobalAttachments,
  useUploadGlobalAttachment,
  useDownloadGlobalAttachment,
  useDeleteGlobalAttachment
} from '../../../hooks/use-cross-portal-attachments';
import { AttachmentType } from '../../../types/api';
import { CrossPortalAttachmentDto, UploadGlobalAttachmentDto } from '../../../services/CrossPortalService';

interface AttachmentsSectionProps {
  requestId: string;
  readOnly?: boolean;
}

export function AttachmentsSection({ requestId, readOnly = false }: AttachmentsSectionProps) {
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [isUploading, setIsUploading] = useState(false);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<FileWithPath | null>(null);
  const [attachmentType, setAttachmentType] = useState<'local' | 'global'>('local');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<CrossPortalAttachmentDto | null>(null);

  const deleteMutation = useDeleteGlobalAttachment();

  // Hook per TUTTI gli allegati - sia locali che globali sono nella tabella shared.CrossPortalAttachments
  // Li separiamo in base al flag isGlobal
  const { data: allAttachments = [], isPending: isInitialLoading } = useGlobalAttachments(parseInt(requestId));
  const uploadGlobalMutation = useUploadGlobalAttachment();
  const downloadGlobalMutation = useDownloadGlobalAttachment();

  // Separa allegati locali (isGlobal=false) e globali (isGlobal=true)
  const localAttachments = allAttachments.filter((a: CrossPortalAttachmentDto) => !a.isGlobal);
  const globalAttachments = allAttachments.filter((a: CrossPortalAttachmentDto) => a.isGlobal);

  const descriptionForm = useForm({
    initialValues: {
      description: ''
    }
  });

  // Gestione upload file locali
  const handleLocalFileUpload = async (files: FileWithPath[]) => {
    if (readOnly) return;

    const file = files[0];
    if (!file) return;

    // Se il file è grande, chiedi una descrizione
    if (file.size > 1024 * 1024) { // > 1MB
      setPendingFile(file);
      setAttachmentType('local');
      setDescriptionModalOpen(true);
      return;
    }

    await uploadLocalFile(file);
  };

  // Gestione upload file globali
  const handleGlobalFileUpload = async (files: FileWithPath[]) => {
    if (readOnly) return;

    const file = files[0];
    if (!file) return;

    // Se il file è grande, chiedi una descrizione
    if (file.size > 1024 * 1024) { // > 1MB
      setPendingFile(file);
      setAttachmentType('global');
      setDescriptionModalOpen(true);
      return;
    }

    await uploadGlobalFile(file);
  };

  const uploadLocalFile = async (file: FileWithPath, description?: string) => {
    setIsUploading(true);
    try {
      // Usa la stessa struttura e endpoint dei globali, ma con IsGlobal: false e senza CrossPortalRequestId
      const uploadData = {
        file: file,
        ufficioStileRequestId: parseInt(requestId),
        uploaderName: 'current-user', // TODO: get from auth context
        uploadedByPortal: 'ufficiostile',
        description: description,
        isGlobal: false // locale
      };
      await uploadGlobalMutation.mutateAsync(uploadData);
    } catch (error) {
      // Notifica di errore già gestita dalla mutation
    } finally {
      setIsUploading(false);
    }
  };

  const uploadGlobalFile = async (file: FileWithPath, description?: string) => {
    setIsUploading(true);

    try {
      const uploadData: UploadGlobalAttachmentDto = {
        file: file,
        ufficioStileRequestId: parseInt(requestId), // Convertito da string a number
        uploaderName: 'current-user', // TODO: get from auth context
        uploadedByPortal: 'ufficiostile',
        description: description,
        isGlobal: true // esplicitamente globale
      };

      await uploadGlobalMutation.mutateAsync(uploadData);

    } catch (error) {
      // Error handling is done in the mutation
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadWithDescription = async () => {
    if (!pendingFile) return;
    const file = pendingFile;
    const type = attachmentType;
    const description = descriptionForm.values.description;

    // Chiudi la modale e resetta lo stato prima dell'upload per evitare flicker
    setDescriptionModalOpen(false);
    setPendingFile(null);
    descriptionForm.reset();

    if (type === 'local') {
      await uploadLocalFile(file, description);
    } else {
      await uploadGlobalFile(file, description);
    }
  };

  // Gestione download - tutti gli allegati usano ora l'endpoint cross-portal
  const handleDownload = async (attachment: CrossPortalAttachmentDto) => {
    try {
      // Tutti gli allegati (locali e globali) sono nella tabella shared.CrossPortalAttachments
      // Usa sempre l'endpoint globale che genera SAS token freschi
      await downloadGlobalMutation.mutateAsync({
        id: (attachment.attachmentId ?? attachment.id ?? '').toString(),
        fileName: (attachment.fileName ?? 'downloaded-file').toString()
      });
    } catch (error) {
      // Error handling is done in the mutations
    }
  };


  const handleDeleteClick = (attachment: CrossPortalAttachmentDto) => {
    setAttachmentToDelete(attachment);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!attachmentToDelete) return;
    const id = (attachmentToDelete.attachmentId ?? attachmentToDelete.id ?? '').toString();
    await deleteMutation.mutateAsync({ id, requestId: parseInt(requestId) });
    setDeleteConfirmOpen(false);
    setAttachmentToDelete(null);
  };

  // Utility functions
  const getFileType = (mimeType: string): AttachmentType => {
    if (mimeType.startsWith('image/')) return AttachmentType.IMAGE;
    if (mimeType.startsWith('video/')) return AttachmentType.VIDEO;
    if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text')) {
      return AttachmentType.DOCUMENT;
    }
    return AttachmentType.OTHER;
  };

  const getFileIcon = (type: AttachmentType | string) => {
    const attachmentType = typeof type === 'string' ? type.toLowerCase() : type;
    
    switch (attachmentType) {
      case AttachmentType.IMAGE:
      case 'image':
        return <IconPhoto size={20} />;
      case AttachmentType.VIDEO:
      case 'video':
        return <IconVideo size={20} />;
      case AttachmentType.DOCUMENT:
      case 'document':
        return <IconFileText size={20} />;
      default:
        return <IconFile size={20} />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAttachmentOrigin = (attachment: CrossPortalAttachmentDto, isGlobal: boolean): { label: string; color: string; icon: React.ReactNode } => {
    if (isGlobal) {
      const portal = attachment.uploadedByPortal?.toLowerCase();
      if (portal === 'personalizzati') {
        return {
          label: 'Da Personalizzati',
          color: 'blue',
          icon: <IconGlobe size={12} />
        };
      } else if (portal === 'configuratore') {
        return {
          label: 'Da Configuratore',
          color: 'orange',
          icon: <IconGlobe size={12} />
        };
      } else {
        return {
          label: 'Globale',
          color: 'purple',
          icon: <IconGlobe size={12} />
        };
      }
    } else {
      return {
        label: 'Locale',
        color: 'green',
        icon: <IconBuilding size={12} />
      };
    }
  };

  const renderAttachmentCard = (attachment: CrossPortalAttachmentDto, index: number) => {
    // Usa attachment.isGlobal per determinare l'origine
    const origin = getAttachmentOrigin(attachment, attachment.isGlobal ?? false);

    // TUTTI gli allegati sono CrossPortalAttachmentDto
    const fileName = attachment.fileName || '';
    const fileSize = attachment.fileSizeBytes || 0;
    const mimeType = attachment.contentType || '';
    const attachmentId = attachment.attachmentId || '';
    const type = getFileType(mimeType);
    const description = attachment.description;

    return (
      <Card key={`attachment-${attachmentId || index}`} withBorder padding="sm">
        <Group justify="space-between">
          <Group>
            {getFileIcon(type)}
            <Box>
              <Group gap="xs" mb={4}>
                <Tooltip label={fileName} multiline w={300} withArrow disabled={!fileName || fileName.length < 30}>
                  <Text fw={500} size="sm" truncate style={{ maxWidth: 200 }}>
                    {fileName}
                  </Text>
                </Tooltip>
                <Badge
                  size="xs"
                  color={origin.color}
                  variant="light"
                  leftSection={origin.icon}
                >
                  {origin.label}
                </Badge>
              </Group>
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {formatFileSize(fileSize || 0)}
                </Text>
                <Badge size="xs" variant="outline">
                  {typeof type === 'string' ? type : AttachmentType[type]}
                </Badge>
                {description && (
                  <Tooltip label={description} multiline w={300} withArrow disabled={!description || description.length < 30}>
                    <Text size="xs" c="dimmed" truncate style={{ maxWidth: 150 }}>
                      {description}
                    </Text>
                  </Tooltip>
                )}
              </Group>
            </Box>
          </Group>

          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color="green"
              onClick={() => handleDownload(attachment)}
              loading={downloadGlobalMutation.isPending}
            >
              <IconDownload size={16} />
            </ActionIcon>
            {!readOnly && (attachment.uploadedByPortal?.toLowerCase() === 'ufficiostile' || attachment.sourcePortal?.toLowerCase() === 'ufficiostile') && (
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => handleDeleteClick(attachment)}
                loading={deleteMutation.isPending}
              >
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        </Group>

        {/* Progress Bar per upload */}
        {uploadProgress[fileName || ''] !== undefined && (
          <Progress
            value={uploadProgress[fileName || '']}
            size="sm"
            mt="xs"
            color={uploadProgress[fileName || ''] === 100 ? 'green' : 'blue'}
          />
        )}
      </Card>
    );
  };

  const totalAttachments = localAttachments.length + globalAttachments.length;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={500} size="lg">Allegati</Text>
        <Badge variant="light" color="blue">
          {totalAttachments} file{totalAttachments !== 1 ? 's' : ''}
        </Badge>
      </Group>

      {!readOnly && (
        <Tabs defaultValue="local" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="local" leftSection={<IconBuilding size="0.8rem" />}>
              Allegato Locale
            </Tabs.Tab>
            <Tabs.Tab value="global" leftSection={<IconGlobe size="0.8rem" />}>
              Allegato Globale
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="local" pt="md">
            <Dropzone
              onDrop={handleLocalFileUpload}
              loading={isUploading}
              multiple={false}
              maxSize={50 * 1024 * 1024} // 50MB
            >
              <Group justify="center" gap="xl" style={{ minHeight: 120 }}>
                <Dropzone.Accept>
                  <IconUpload size={50} color="green" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={50} color="red" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconUpload size={50} />
                </Dropzone.Idle>

                <div>
                  <Text size="xl" inline>
                    Trascina file locale qui o clicca per selezionare
                  </Text>
                  <Text size="sm" c="dimmed" inline mt={7}>
                    Dimensione massima: 50MB • Visibile solo in Ufficio Stile
                  </Text>
                </div>
              </Group>
            </Dropzone>
          </Tabs.Panel>

          <Tabs.Panel value="global" pt="md">
            <Dropzone
              onDrop={handleGlobalFileUpload}
              loading={isUploading}
              multiple={false}
              maxSize={50 * 1024 * 1024} // 50MB
            >
              <Group justify="center" gap="xl" style={{ minHeight: 120 }}>
                <Dropzone.Accept>
                  <IconUpload size={50} color="green" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={50} color="red" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconUpload size={50} />
                </Dropzone.Idle>

                <div>
                  <Text size="xl" inline>
                    Trascina file globale qui o clicca per selezionare
                  </Text>
                  <Text size="sm" c="dimmed" inline mt={7}>
                    Dimensione massima: 50MB • Visibile in tutti i portali
                  </Text>
                </div>
              </Group>
            </Dropzone>
          </Tabs.Panel>
        </Tabs>
      )}

      {/* Lista Allegati */}
      <Stack gap="sm">
        {/* Allegati Locali (isGlobal=false) */}
        {localAttachments.length > 0 && (
          <>
            <Divider label="Allegati Locali" labelPosition="center" />
            {localAttachments.map((attachment: CrossPortalAttachmentDto, index: number) =>
              renderAttachmentCard(attachment, index)
            )}
          </>
        )}

        {/* Allegati Globali (isGlobal=true) */}
        {globalAttachments.length > 0 && (
          <>
            <Divider label="Allegati Globali" labelPosition="center" />
            {globalAttachments.map((attachment: CrossPortalAttachmentDto, index: number) =>
              renderAttachmentCard(attachment, index)
            )}
          </>
        )}

        {totalAttachments === 0 && !isInitialLoading && (
          <Alert color="gray" variant="light">
            <Text size="sm" c="dimmed" ta="center">
              Nessun allegato presente
            </Text>
          </Alert>
        )}

        {isInitialLoading && (
          <Alert color="blue" variant="light">
            <Text size="sm" c="dimmed" ta="center">
              Caricamento allegati...
            </Text>
          </Alert>
        )}
      </Stack>

      {/* Modal Conferma Eliminazione */}
      <Modal
        opened={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setAttachmentToDelete(null);
        }}
        title="Conferma eliminazione allegato"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Sei sicuro di voler eliminare l'allegato <strong>{attachmentToDelete?.fileName}</strong>?
          </Text>
          <Text size="xs" c="dimmed">
            Questa azione non può essere annullata.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setAttachmentToDelete(null);
              }}
            >
              Annulla
            </Button>
            <Button
              color="red"
              onClick={handleConfirmDelete}
              loading={deleteMutation.isPending}
            >
              Elimina
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal Descrizione Upload */}
      <Modal
        opened={descriptionModalOpen}
        onClose={() => {
          setDescriptionModalOpen(false);
          setPendingFile(null);
          descriptionForm.reset();
        }}
        title={`Aggiungi descrizione file ${attachmentType === 'global' ? 'globale' : 'locale'}`}
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            File: {pendingFile?.name} ({pendingFile ? formatFileSize(pendingFile.size) : ''})
          </Text>
          
          <TextInput
            label="Descrizione (opzionale)"
            placeholder="Inserisci una descrizione per questo file..."
            {...descriptionForm.getInputProps('description')}
          />

          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => {
                setDescriptionModalOpen(false);
                setPendingFile(null);
                descriptionForm.reset();
              }}
            >
              Annulla
            </Button>
            <Button
              onClick={handleUploadWithDescription}
              loading={isUploading}
            >
              Carica File {attachmentType === 'global' ? 'Globale' : 'Locale'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
